import type { ReactNode } from 'react';
import { fmtPct } from '@/features/notas/calculo';
import { Alvo, AreaGrafico, barraPath, COR } from './base';

export interface LinhaComparacao {
  id: string;
  rotulo: string;
  /** fração de 0 a 1 */
  voce: number | null;
  turma: number | null;
  detalhe?: ReactNode;
}

/** Barra do aluno (cor da marca) com um traço na média da turma, em % do máximo de cada item. */
export function BarrasComparacao({ linhas, rotulo }: { linhas: LinhaComparacao[]; rotulo: string }) {
  const alt = 34;
  const altura = linhas.length * alt + 8;
  return (
    <AreaGrafico altura={altura} rotulo={rotulo}>
      {(w) => {
        const compacto = w < 520;
        const esq = compacto ? 0 : Math.min(260, w * 0.42);
        const dir = 92;
        const larg = w - esq - dir;
        const x = (f: number) => esq + Math.max(0, Math.min(1, f)) * larg;
        return (
          <g>
            {linhas.map((l, i) => {
              const y0 = i * alt + 4;
              const barraY = compacto ? y0 + 17 : y0 + 9;
              const barraH = compacto ? 9 : 14;
              return (
                <Alvo
                  key={l.id}
                  titulo={
                    <>
                      <strong>{l.rotulo}</strong>
                      <br />
                      Você: {fmtPct(l.voce)} · média da turma: {fmtPct(l.turma)}
                      {l.detalhe && (
                        <>
                          <br />
                          {l.detalhe}
                        </>
                      )}
                    </>
                  }
                >
                  <g>
                    <rect x={0} y={y0} width={w} height={alt} fill="transparent" />
                    <text
                      x={0}
                      y={compacto ? y0 + 9 : y0 + 16}
                      dy={compacto ? '0.2em' : '0.32em'}
                      fontSize={compacto ? 11.5 : 13}
                      fill={COR.texto}
                    >
                      {encurta(l.rotulo, compacto ? 52 : Math.floor(esq / 6.6))}
                    </text>
                    <rect x={x(0)} y={barraY} width={larg} height={barraH} rx={barraH / 2} fill={COR.trilho} />
                    {l.voce !== null && l.voce > 0 && (
                      <path d={barraPath(x(0), barraY, x(l.voce) - x(0), barraH, barraH / 2, true)} fill={COR.voce} />
                    )}
                    {l.turma !== null && (
                      <line
                        x1={x(l.turma)}
                        x2={x(l.turma)}
                        y1={barraY - 4}
                        y2={barraY + barraH + 4}
                        stroke={COR.texto}
                        strokeWidth={2}
                      />
                    )}
                    <text x={w} y={barraY + barraH / 2} dy="0.32em" textAnchor="end" fontSize={12.5} fill={COR.texto}>
                      <tspan fontWeight={700}>{fmtPct(l.voce)}</tspan>
                      <tspan fill={COR.textoFraco}> · {fmtPct(l.turma)}</tspan>
                    </text>
                  </g>
                </Alvo>
              );
            })}
          </g>
        );
      }}
    </AreaGrafico>
  );
}

const encurta = (s: string, n: number) => (s.length > n ? `${s.slice(0, Math.max(n - 1, 4))}…` : s);
